import React, { Component } from "react";
import { t } from "../../../components/CustomComponent.js";
import { AMENITIES, FACILITIES, TAGS } from "../../../components/asset_option.js";
import ICON from "../icons.js";
import { money, num, isRent, moveInTotal, monthWord } from "../property_util.js";

/* ============================================================================
 * PropertyBody — เนื้อหาหลักใต้หัวข้อประกาศ
 *   1. รายละเอียด (ย่อไว้ 4 บรรทัด กด "ดูเพิ่มเติม" เพื่อกางเต็ม)
 *   2. จุดเด่นของทรัพย์ (แท็กจากฟอร์มลงทรัพย์)
 *   3. สิ่งอำนวยความสะดวก — แยก "ในห้อง" กับ "ส่วนกลางของโครงการ"
 *   4. ค่าใช้จ่ายและเงื่อนไข (เฉพาะประกาศเช่า) พร้อมยอดแรกเข้าที่คิดให้แล้ว
 *   5. จุดสนใจใกล้เคียง
 *   6. ไฟล์แนบที่เปิดเผยได้ : แผนผังห้อง / วิดีโอ
 *
 * รายละเอียดมีได้ 3 ภาษา (ไทย/อังกฤษ/จีน) — โชว์ตามภาษาที่ผู้ใช้เลือกในเว็บก่อน
 * ถ้าภาษานั้นยังไม่ได้เขียนไว้ ค่อยถอยไปใช้ภาษาไทย
 * ========================================================================== */

/* ไอคอนของจุดสนใจใกล้เคียงแต่ละประเภท */
const NEARBY_ICON = {
  transit: ICON.train,
  mall: ICON.cart,
  hospital: ICON.hospital,
  school: ICON.school,
  park: ICON.tree,
};

/* หัวข้อย่อยของแต่ละกล่อง */
const Block = ({ icon, title, note, children, id }) => (
  <section className="hm-pd-block" id={id}>
    <div className="hm-pd-block__head">
      <h2 className="hm-pd-block__title">
        <span className="hm-ico">{icon}</span>
        {title}
      </h2>
      {!!note && <span className="hm-pd-block__note">{note}</span>}
    </div>
    {children}
  </section>
);

export default class PropertyBody extends Component {
  state = { expand: false };

  /* เลือกภาษาของรายละเอียดตามภาษาที่ผู้ใช้ตั้งไว้ ถอยกลับเป็นไทยถ้ายังไม่มีคำแปล */
  description = () => {
    const d = this.props.data;
    const lang = localStorage.getItem("lang") || "TH";
    const byLang = { TH: d.description_th, EN: d.description_en, CN: d.description_cn };
    return (byLang[lang] || "").trim() || (d.description_th || "").trim();
  };

  render() {
    const { data } = this.props;
    const { expand } = this.state;

    const desc = this.description();
    const lines = desc.split("\n").filter(Boolean);
    const long = lines.length > 4 || desc.length > 320;

    const amenities = AMENITIES.filter((e) => (data.amenities || []).includes(e.value));
    const facilities = FACILITIES.filter((e) => (data.facilities || []).includes(e.value));
    const tags = (data.tags || []).map((value) => TAGS.find((e) => e.value === value)?.label || value);
    const rent = isRent(data.deals);

    return (
      <div className="hm-pd-body">
        {/* ------------------------------------------------------ รายละเอียด */}
        {!!desc && (
          <Block icon={ICON.file} title={t("detail_description")} id="pd-desc">
            <div className={`hm-pd-desc ${expand || !long ? "is-open" : ""}`}>
              {lines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            {long && (
              <button
                type="button"
                className="hm-pd-more"
                onClick={() => this.setState({ expand: !expand })}
              >
                {t(expand ? "detail_read_less" : "detail_read_more")}
                <span className="hm-ico">{expand ? ICON.chevron_up : ICON.chevron_down}</span>
              </button>
            )}
          </Block>
        )}

        {/* ------------------------------------------------ จุดเด่นของทรัพย์ */}
        {!!tags.length && (
          <Block icon={ICON.star} title={t("detail_highlights")}>
            <div className="hm-pd-tags">
              {tags.map((label) => (
                <span key={label} className="hm-pd-tag">
                  <span className="hm-ico">{ICON.check}</span>
                  {t(label)}
                </span>
              ))}
            </div>
          </Block>
        )}

        {/* ------------------------------------ สิ่งอำนวยความสะดวก (ส่วนกลาง) */}
        {!!facilities.length && (
          <Block
            icon={ICON.building}
            title={t("detail_facilities")}
            note={t("detail_facilities_note")}
          >
            <div className="hm-pd-icongrid">
              {facilities.map((item) => (
                <div key={item.value} className="hm-pd-icongrid__item">
                  <span className="hm-pd-icongrid__ico hm-ico">{item.icon}</span>
                  {/* ติ๊ก "อื่น ๆ" ไว้ = โชว์ข้อความที่ผู้ดูแลพิมพ์เอง แทนคำว่า "อื่น ๆ" ลอย ๆ */}
                  <span className="hm-pd-icongrid__label">
                    {item.value === "other" && data.facility_other ? data.facility_other : t(item.label)}
                  </span>
                </div>
              ))}
            </div>
          </Block>
        )}

        {/* -------------------------------------- สิ่งอำนวยความสะดวก (ในห้อง) */}
        {!!amenities.length && (
          <Block icon={ICON.home} title={t("detail_amenities")} note={t("detail_amenities_note")}>
            <div className="hm-pd-icongrid hm-pd-icongrid--soft">
              {amenities.map((item) => (
                <div key={item.value} className="hm-pd-icongrid__item">
                  <span className="hm-pd-icongrid__ico hm-ico">{item.icon}</span>
                  <span className="hm-pd-icongrid__label">
                    {item.value === "other" && data.amenity_other ? data.amenity_other : t(item.label)}
                  </span>
                </div>
              ))}
            </div>
          </Block>
        )}

        {/* ------------------------------------------- ค่าใช้จ่ายและเงื่อนไข */}
        {rent && (
          <Block icon={ICON.money} title={t("detail_cost")} note={t("detail_cost_note")}>
            <div className="hm-pd-cost">
              <ul className="hm-pd-cost__list">
                <li>
                  <span>{t("detail_cost_rent")}</span>
                  <b>
                    {money(data.rent)} {t("baht_per_month")}
                  </b>
                </li>
                {!!num(data.deposit) && (
                  <li>
                    <span>{t("detail_cost_deposit")}</span>
                    <b>{money(data.deposit)} {t("unit_baht")}</b>
                  </li>
                )}
                {!!num(data.advance_month) && (
                  <li>
                    <span>{t("detail_cost_advance")}</span>
                    <b>
                      {data.advance_month} {t("month_unit")} ({money(num(data.advance_month) * num(data.rent))} {t("unit_baht")})
                    </b>
                  </li>
                )}
                {/* ระบุจำนวนเงินไว้ให้โชว์จำนวนจริง — ทรัพย์เก่าที่กรอกไว้แค่จำนวนเดือน
                    ยังตกไปที่ข้อความเดิม "เจ้าของชำระ" เหมือนก่อน */}
                {(!!num(data.common_fee) || !!num(data.common_fee_month)) && (
                  <li>
                    <span>{t("detail_cost_common_fee")}</span>
                    <b>
                      {num(data.common_fee)
                        ? `${money(data.common_fee)} ${t("baht_per_month")}${data.common_fee_included ? ` · ${t("detail_cost_included_in_rent")}` : ""}`
                        : t("detail_cost_owner_paid")}
                    </b>
                  </li>
                )}
                {!!num(data.water_fee) && (
                  <li>
                    <span>{t("detail_cost_water")}</span>
                    <b>{money(data.water_fee)} {t("baht_per_month")}</b>
                  </li>
                )}
                {!!data.electric_fee && (
                  <li>
                    <span>{t("detail_cost_electric")}</span>
                    <b>{t(data.electric_fee)}</b>
                  </li>
                )}
                {!!num(data.min_contract_month) && (
                  <li>
                    <span>{t("detail_min_contract")}</span>
                    <b>{monthWord(data.min_contract_month)}</b>
                  </li>
                )}
              </ul>

              {/* ยอดแรกเข้า — คิดรวมให้เลย เพราะเป็นคำถามแรกของผู้เช่าเกือบทุกราย */}
              <div className="hm-pd-cost__total">
                <span className="hm-pd-cost__total-label">
                  {t("detail_move_in_total")}
                  <small>{t("detail_move_in_hint")}</small>
                </span>
                <span className="hm-pd-cost__total-value">
                  {money(moveInTotal(data))} <small>{t("unit_baht")}</small>
                </span>
              </div>

              {/* เงื่อนไขพิเศษของประกาศ — ต้องอ่านคู่กับค่าใช้จ่าย ไม่ใช่ไปซ่อนอยู่ท้ายคำบรรยาย */}
              {!!data.special_terms && (
                <p className="hm-pd-cost__note">
                  <span className="hm-ico">{ICON.file}</span>
                  {data.special_terms}
                </p>
              )}
            </div>
          </Block>
        )}

        {/* ------------------------------------------------ จุดสนใจใกล้เคียง */}
        {!!(data.nearby || []).length && (
          <Block icon={ICON.marker} title={t("detail_nearby")}>
            <div className="hm-pd-nearby">
              {data.nearby.map((item) => (
                <div key={item.label} className="hm-pd-nearby__item">
                  <span className={`hm-pd-nearby__ico is-${item.type} hm-ico`}>{NEARBY_ICON[item.type] || ICON.marker}</span>
                  <span className="hm-pd-nearby__label">{item.label}</span>
                  <span className="hm-pd-nearby__dist">
                    {item.distance < 1 ? `${Math.round(item.distance * 1000)} ${t("detail_unit_m")}` : `${item.distance} ${t("detail_unit_km")}`}
                  </span>
                </div>
              ))}
            </div>
          </Block>
        )}

        {/* ------------------------------------------- แผนผังห้อง / วิดีโอ */}
        {(!!(data.floor_plans || []).length || !!data.video_url) && (
          <Block icon={ICON.image} title={t("detail_media")}>
            <div className="hm-pd-media">
              {(data.floor_plans || []).map((item) => (
                <button
                  key={item.uid}
                  type="button"
                  className="hm-pd-media__item"
                  onClick={() => this.props.onFloorPlan && this.props.onFloorPlan(item)}
                >
                  <span className="hm-pd-media__ico hm-ico">{ICON.ruler}</span>
                  <span className="hm-pd-media__text">
                    <b>{t("detail_floor_plan")}</b>
                    <small>{item.name} · {item.size}</small>
                  </span>
                  <span className="hm-ico hm-pd-media__go">{ICON.chevron_right_small}</span>
                </button>
              ))}

              {!!data.video_url && (
                <button
                  type="button"
                  className="hm-pd-media__item"
                  onClick={() => this.props.onVideo && this.props.onVideo(data.video_url)}
                >
                  <span className="hm-pd-media__ico hm-ico">{ICON.play}</span>
                  <span className="hm-pd-media__text">
                    <b>{t("detail_video")}</b>
                    <small>{t("detail_video_hint")}</small>
                  </span>
                  <span className="hm-ico hm-pd-media__go">{ICON.chevron_right_small}</span>
                </button>
              )}
            </div>
          </Block>
        )}
      </div>
    );
  }
}
